import React from 'react';
import Link from 'next/link';
import { srOnly } from '@domain-group/fe-brary';
import CardBanner from './card-banner';
import * as styles from './search-result-card.style';

export interface SearchResultCardProps {
  profileUrl: string;
  name: string;
  agencyName: string;
  logoUrl?: string;
  logoColour?: string;
  priority?: boolean;
  subtitle?: React.ReactNode;
  officeLine?: React.ReactNode;
  officeLineAsHeading?: boolean;
  hideOfficeLineOnMobile?: boolean;
  media?: React.ReactNode;
  stackMediaOnMobile?: boolean;
  rating?: React.ReactNode;
  mobileRating?: React.ReactNode;
  metricsLabel?: React.ReactNode;
  metrics?: React.ReactNode;
  statsColumns?: number;
  footnote?: React.ReactNode;
  footer?: React.ReactNode;
  selected?: boolean;
  onViewProfile?: () => void;
}

// Shared shell for the 2026 search result cards (agent + agency).
// Owns layout/responsiveness/interaction states only - metrics, media and
// title content are supplied by the agent/agency card implementations.
const SearchResultCard = ({
  profileUrl,
  name,
  agencyName,
  logoUrl,
  logoColour,
  priority = false,
  subtitle,
  officeLine,
  officeLineAsHeading = false,
  hideOfficeLineOnMobile = false,
  media,
  stackMediaOnMobile = false,
  rating,
  mobileRating,
  metricsLabel,
  metrics,
  statsColumns = 4,
  footnote,
  footer,
  selected = false,
  onViewProfile,
}: SearchResultCardProps): JSX.Element => {
  const OfficeLine = officeLineAsHeading ? 'h3' : 'p';

  return (
    <div css={styles.container({ selected })} data-testid="search-result-card">
      <CardBanner
        agencyName={agencyName}
        logoUrl={logoUrl}
        logoColour={logoColour}
        priority={priority}
      />
      <div css={styles.body}>
        <Link
          href={profileUrl}
          css={styles.link}
          onClick={onViewProfile}
          data-testid="search-result-card__link"
        >
          <span css={srOnly}>View {name}&apos;s profile</span>
        </Link>
        <div css={styles.headerRow}>
          <div css={styles.title}>
            <h2 css={styles.name}>{name}</h2>
            {subtitle && <p css={styles.subtitle}>{subtitle}</p>}
            {officeLine && (
              <OfficeLine
                css={
                  hideOfficeLineOnMobile
                    ? styles.officeLineHiddenOnMobile
                    : styles.officeLine
                }
              >
                {officeLine}
              </OfficeLine>
            )}
            {mobileRating && (
              <div css={styles.mobileRating}>{mobileRating}</div>
            )}
          </div>
          {media && (
            <div
              css={stackMediaOnMobile ? styles.mediaStacksOnMobile : undefined}
            >
              {media}
            </div>
          )}
        </div>
        {(metricsLabel || rating || metrics || footnote) && (
          <div css={styles.statsSection}>
            {metricsLabel && <p css={styles.metricsLabel}>{metricsLabel}</p>}
            {(rating || metrics) && (
              <div css={styles.statsRow(statsColumns)}>
                {rating && <div css={styles.rating}>{rating}</div>}
                {metrics}
              </div>
            )}
            {footnote && <p css={styles.footnote}>{footnote}</p>}
          </div>
        )}
        {footer && <div css={styles.footer}>{footer}</div>}
      </div>
    </div>
  );
};

export default SearchResultCard;
